"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AccessTokenProvider = void 0;
var tslib_1 = require("tslib");
var tokenDurationMs = 10 * 60 * 1000; // 10 minutes
var leewayMs = 30 * 1000; // 30 sec
var checkIntervalMs = 1 * 1000; // 1 sec
// An AccessTokenProvider is initialised with an AccessTokenFetcher, and uses it
// in order to keep a valid access token available and refresh it before it has
// expired.
var AccessTokenProvider = /** @class */ (function () {
    function AccessTokenProvider(fetchAccessToken) {
        this.fetchAccessToken = fetchAccessToken;
        this.pendingRequests = [];
        this.refreshInProgress = false;
        this.cancelRefresh = false;
        this.stopped = false;
    }
    // This method should be called first.  A valid access token should be
    // available once the promise it returns is resolved.
    AccessTokenProvider.prototype.start = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.refreshToken()];
                    case 1:
                        _b.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        _a = _b.sent();
                        // Exception can be raised by things like request blocking or network issues.
                        return [2 /*return*/];
                    case 3:
                        this.keepTokenRefreshed();
                        return [2 /*return*/];
                }
            });
        });
    };
    // Stop refreshing the access token. This can be called if we know the user is not authorised
    // to get a token, so we do not want to retry fetching it any more.
    AccessTokenProvider.prototype.stop = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                this.stopped = true;
                this.cancelRefresh = true;
                if (this.interval) {
                    clearInterval(this.interval);
                }
                return [2 /*return*/];
            });
        });
    };
    // Returns (a promise for) an access token. Blocks until one is available.
    // Do not keep hold of these, but call this function each time an access token is needed
    // as access tokens are automatically refreshed.
    AccessTokenProvider.prototype.requestAccessToken = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                if (this.stopped) {
                    // If stopped is true then we've permanently given up requesting access tokens. The caller of this class should
                    // know this, but sometimes further requests are made anyway. In this case, we return a promise that will never
                    // resolve as it prevents the client from making requests with a bad access token.
                    return [2 /*return*/, this.addPendingRequest()];
                }
                // NOTE: We explicitly check to see if accessToken is undefined here to appease the type checker, but shouldRefreshToken will also check this.
                if (!this.accessToken || this.shouldRefreshToken()) {
                    this.refreshToken();
                    return [2 /*return*/, this.addPendingRequest()];
                }
                return [2 /*return*/, this.accessToken];
            });
        });
    };
    AccessTokenProvider.prototype.addPendingRequest = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.pendingRequests.push(resolve);
        });
    };
    AccessTokenProvider.prototype.keepTokenRefreshed = function () {
        var _this = this;
        if (this.interval) {
            clearInterval(this.interval);
        }
        this.interval = setInterval(function () {
            if (_this.shouldRefreshToken()) {
                _this.refreshToken();
            }
        }, checkIntervalMs);
    };
    AccessTokenProvider.prototype.shouldRefreshToken = function () {
        // Keeps track of the time according to the device clock to guard against
        // the performance timer being stopped.
        var clockDiff = Date.now() - performance.now();
        var clockDiffDrift = this.clockDiff
            ? Math.abs(this.clockDiff - clockDiff)
            : 0;
        if (!this.accessToken) {
            // We don't have a token
            return true;
        }
        if (this.refreshTime === undefined) {
            // We don't know what the refresh time is
            return true;
        }
        if (performance.now() > this.refreshTime) {
            // We have exceeded the refresh time
            return true;
        }
        if (clockDiffDrift > 5 * checkIntervalMs) {
            // The clockDiff has exceeded 5s drift.  This could be due to a change to
            // the clock (we use the absolute value in case the difference is
            // negative) but could also be because the performance timer has stopped
            // incrementing for more than 5s, e.g. because the device went to sleep.
            return true;
        }
        // There is no reason why the token should be invalid.
        return false;
    };
    AccessTokenProvider.prototype.refreshToken = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var clockDiff, refreshTime, res, token, pendingRequests, pendingRequests_1, pendingRequests_1_1, resolve, _a;
            var e_1, _b;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        // Prevent concurrently refreshing tokens
                        if (this.refreshInProgress) {
                            return [2 /*return*/];
                        }
                        this.refreshInProgress = true;
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 5, 6, 7]);
                        clockDiff = Date.now() - performance.now();
                        refreshTime = performance.now() + tokenDurationMs - leewayMs;
                        return [4 /*yield*/, this.fetchAccessToken()];
                    case 2:
                        res = _c.sent();
                        if (!res.ok) return [3 /*break*/, 4];
                        return [4 /*yield*/, res.text()];
                    case 3:
                        token = _c.sent();
                        // If the token was invalidated during the refresh, then this token is
                        // not known to be good so we schedule getting another token ASAP.
                        if (this.cancelRefresh) {
                            setTimeout(this.refreshToken, 0);
                            return [2 /*return*/];
                        }
                        this.accessToken = token;
                        // Set next refresh to shortly before expiration
                        this.refreshTime = refreshTime;
                        this.clockDiff = clockDiff;
                        pendingRequests = this.pendingRequests;
                        this.pendingRequests = [];
                        try {
                            for (pendingRequests_1 = tslib_1.__values(pendingRequests), pendingRequests_1_1 = pendingRequests_1.next(); !pendingRequests_1_1.done; pendingRequests_1_1 = pendingRequests_1.next()) {
                                resolve = pendingRequests_1_1.value;
                                resolve(token);
                            }
                        }
                        catch (e_1_1) { e_1 = { error: e_1_1 }; }
                        finally {
                            try {
                                if (pendingRequests_1_1 && !pendingRequests_1_1.done && (_b = pendingRequests_1.return)) _b.call(pendingRequests_1);
                            }
                            finally { if (e_1) throw e_1.error; }
                        }
                        _c.label = 4;
                    case 4: return [3 /*break*/, 7];
                    case 5:
                        _a = _c.sent();
                        // Exception can be raised by things like request blocking or network issues.
                        return [2 /*return*/];
                    case 6:
                        this.refreshInProgress = false;
                        this.cancelRefresh = false;
                        return [7 /*endfinally*/];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    return AccessTokenProvider;
}());
exports.AccessTokenProvider = AccessTokenProvider;
